import React from 'react';
import { FaAngleDoubleLeft, FaAngleLeft, FaAngleRight, FaAngleDoubleRight } from 'react-icons/fa';

// KISS: 고급 페이지네이션 props
interface AdvancedPaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  showInfo?: boolean;
}

export const AdvancedPagination: React.FC<AdvancedPaginationProps> = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20],
  showInfo = true
}) => {
  // KISS: 계산된 값들
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = Math.min(totalItems, (currentPage - 1) * pageSize + 1);
  const endItem = Math.min(totalItems, currentPage * pageSize);
  
  // KISS: 페이지 번호 배열 생성 (최대 5개 표시)
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // 총 페이지가 5개 이하면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 복잡한 로직 (현재 페이지 주변 표시)
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };
  
  const pageNumbers = getPageNumbers();
  
  // 버튼 클릭 핸들러들
  const handleFirstPage = () => onPageChange(1);
  const handlePrevPage = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages, currentPage + 1));
  const handleLastPage = () => onPageChange(totalPages);
  
  if (totalItems === 0) {
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
        데이터가 없습니다.
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
      {/* KISS: 왼쪽 - 페이지 크기 선택 */}
      <div className="flex items-center space-x-2">
        <span className="text-sm text-gray-700 dark:text-gray-300">표시:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>{size}개</option>
          ))}
        </select>
      </div>
      
      {/* KISS: 가운데 - 페이지 네비게이션 */}
      <div className="flex items-center space-x-1">
        {/* 첫 페이지 */}
        <button
          onClick={handleFirstPage}
          disabled={currentPage === 1}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          title="첫 페이지"
        >
          <FaAngleDoubleLeft className="h-4 w-4" />
        </button>
        
        {/* 이전 페이지 */}
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          title="이전 페이지"
        >
          <FaAngleLeft className="h-4 w-4" />
        </button>
        
        {/* 페이지 번호들 */}
        {pageNumbers.map((pageNum, index) => (
          typeof pageNum === 'number' ? (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === pageNum
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              {pageNum}
            </button>
          ) : (
            <span key={`ellipsis-${index}`} className="px-2 py-2 text-gray-500 dark:text-gray-400">
              {pageNum}
            </span>
          )
        ))}
        
        {/* 다음 페이지 */}
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          title="다음 페이지"
        >
          <FaAngleRight className="h-4 w-4" />
        </button>
        
        {/* 마지막 페이지 */}
        <button
          onClick={handleLastPage}
          disabled={currentPage === totalPages}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          title="마지막 페이지"
        >
          <FaAngleDoubleRight className="h-4 w-4" />
        </button>
      </div>
      
      {/* KISS: 오른쪽 - 정보 표시 */}
      {showInfo && (
        <div className="text-sm text-gray-700 dark:text-gray-300">
          총 <span className="font-medium text-blue-600 dark:text-blue-400">{totalItems}</span>건 중{' '}
          <span className="font-medium">{startItem}-{endItem}</span>
        </div>
      )}
    </div>
  );
};